import React, { useState, useCallback, useEffect } from 'react';
import { 
  Autocomplete, 
  TextField, 
  Grid, 
  CircularProgress,
  Typography,
  Tooltip,
  Box
} from '@mui/material';
import { Info } from 'lucide-react';
import debounce from 'lodash/debounce';
import axiosInstance from '../../utils/axiosConfig';
import { Strand } from '../../types/Strand';
import { IMC_EXPENSE_CODES } from '../../constants/imcCodes';
import { EventType } from '../../types/EventTypes';

interface InternalUseSectionProps {
  activityValue: string;
  activityCodeValue?: string;
  imcCodeValue: string;
  onActivityChange: (value: string, code?: string) => void;
  onIMCCodeChange: (value: string) => void;
}

// Define the IMC option type
interface IMCOption {
  code: string;
  label: string;
  description: string;
}

// Create a minimal Strand object for initialization
const createInitialStrand = (name: string): Strand => ({
  name,
  _id: '',
  isQuickStrand: false,
  description: '',
  type: 'domestic',
  startDate: new Date(),
  endDate: new Date(),
  eventType: 'concert' as EventType,
  status: 'planned',
  mainImage: '',
  dates: [],
  published: false,
  year: new Date().getFullYear().toString(),
  artsCouncilActivityType: '',
  artsCouncilActivityTarget: '',
  artsCouncilStatus: '',
  overallFinance: {
    totalIncome: 0,
    totalExpenditure: 0
  },
  associations: {
    venues: [],
    performances: [],
    strands: [],
    personnel: [],
    bands: [],
    applications: []
  },
  employmentFigures: {
    artists: 0,
    others: 0,
    associatedEmploy: 0,
    volunteers: 0
  },
  audiences: {
    venuesCaps: 0,
    paying: 0,
    free: 0,
    total: 0,
    ratio: 0
  },
  numberOfProposedEvents: 0,
  code: ''
});

const InternalUseSection: React.FC<InternalUseSectionProps> = ({
  activityValue,
  activityCodeValue,
  imcCodeValue,
  onActivityChange,
  onIMCCodeChange
}) => {
  const [strands, setStrands] = useState<Strand[]>([]);
  const [loadingStrands, setLoadingStrands] = useState(false);
  const [strandError, setStrandError] = useState<string | null>(null);
  const [selectedStrand, setSelectedStrand] = useState<Strand | null>(() => 
    activityValue ? createInitialStrand(activityValue) : null
  );

  // Format IMC codes for autocomplete - MOVED UP before useState
  const imcOptions = Object.values(IMC_EXPENSE_CODES).map(code => ({
    code: code.code,
    label: code.label,
    description: code.description
  }));

  // FIXED: Initialize selectedIMCCode with proper default to code 114
  const [selectedIMCCode, setSelectedIMCCode] = useState<IMCOption | null>(() => {
    // If imcCodeValue is provided, use it
    if (imcCodeValue) {
      return { code: '114', label: imcCodeValue, description: '' };
    }
    
    // Otherwise, default to code 114 from the options
    const defaultOption = imcOptions.find(option => option.code === '114');
    if (defaultOption) {
      return defaultOption;
    }
    
    return null;
  });

  // Helper to split activity value into name and year
  const parseActivityValue = (value: string) => {
    const regex = /^(.*?)(?:\s*\((\d{4})\))?$/;
    const match = value.match(regex);
    if (match) {
      const [_, name, year] = match;
      return { name, year: year || '' };
    }
    return { name: value, year: '' };
  };

  // Function to fetch a strand by ID
  const fetchStrandById = async (strandId: string) => {
    try {
      console.log('Fetching full strand with ID:', strandId);
      const response = await axiosInstance.get(`/api/strands/${strandId}`);
      console.log('Full strand data by ID:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching strand by ID:', error);
      return null;
    }
  };

  // Initialize strand on first render
  useEffect(() => {
    if (!activityValue) return;
    
    const initializeStrand = async () => {
      console.log('Initializing strand with activity value:', activityValue);
      
      // Extract name and year from activityValue
      const { name, year } = parseActivityValue(activityValue);
      console.log('Parsed activity value:', { name, year });
      
      if (!name) return;
      
      try {
        setLoadingStrands(true);
        
        // Search for strands with the base name only
        const response = await axiosInstance.get(`/api/strands/search?q=${name}`);
        const candidateStrands = response.data;
        console.log('Found candidate strands:', candidateStrands);
        
        if (candidateStrands.length > 0) {
          // If we have a year, try to find the right strand
          let targetStrand = null;
          
          if (year) {
            // Find the strand with matching year
            targetStrand = candidateStrands.find((s: Strand) => s.year === year);
          } else {
            // Use the first strand if no year specified
            targetStrand = candidateStrands[0];
          }
          
          if (targetStrand) {
            console.log('Found target strand:', targetStrand);
            
            // If we have the ID but not the code, fetch the full strand
            if (targetStrand._id && !targetStrand.code) {
              console.log('Fetching full strand to get code');
              const fullStrand = await fetchStrandById(targetStrand._id);
              if (fullStrand) {
                console.log('Setting selected strand with code:', fullStrand.code);
                setSelectedStrand(fullStrand);
                onActivityChange(fullStrand.name, fullStrand.code);
                return;
              }
            } else if (targetStrand.code) {
              // If we already have the code, use it
              console.log('Using code from search result:', targetStrand.code);
              setSelectedStrand(targetStrand);
              onActivityChange(targetStrand.name, targetStrand.code);
              return;
            }
            
            // If we get here, use the targetStrand without code
            setSelectedStrand(targetStrand);
            onActivityChange(targetStrand.name, '');
          } else {
            // If no matching strand found, use the initial values
            console.log('No matching strand found, using initial name only');
            const initialStrand = createInitialStrand(name);
            setSelectedStrand(initialStrand);
          }
        } else {
          // No strands found, use the initial values
          console.log('No strands found, using initial name only');
          const initialStrand = createInitialStrand(name);
          setSelectedStrand(initialStrand);
        }
      } catch (error) {
        console.error('Error initializing strand:', error);
        // Fall back to initial name
        const initialStrand = createInitialStrand(name);
        setSelectedStrand(initialStrand);
      } finally {
        setLoadingStrands(false);
      }
    };
    
    initializeStrand();
  }, [activityValue]);

  // ADDED: Set default IMC code and trigger onChange when component mounts
  useEffect(() => {
    if (!imcCodeValue && selectedIMCCode && selectedIMCCode.code === '114') {
      // Trigger the onChange callback with the default code
      onIMCCodeChange(selectedIMCCode.label);
    }
  }, []);
  
  // Update selectedStrand when activityCodeValue changes
  useEffect(() => {
    if (activityCodeValue && selectedStrand && selectedStrand.code !== activityCodeValue) {
      console.log('Updating selected strand with code:', activityCodeValue);
      setSelectedStrand(prev => 
        prev ? { ...prev, code: activityCodeValue } : null
      );
    }
  }, [activityCodeValue, selectedStrand]);

  // Initialize IMC code
  useEffect(() => {
    if (imcCodeValue && !selectedIMCCode) {
      setSelectedIMCCode({ code: '', label: imcCodeValue, description: '' });
    }
  }, [imcCodeValue, selectedIMCCode]);

  // Debounced strand search
  const searchStrands = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2) return;

      setLoadingStrands(true);
      setStrandError(null);

      try {
        // Extract name and year if present
        const { name, year } = parseActivityValue(searchTerm);
        console.log('Searching for strands with parsed terms:', { name, year });
        
        // Use only the name part for searching
        const response = await axiosInstance.get(`/api/strands/search?q=${name}`);
        const allStrands = response.data;
        console.log('All strands from search:', allStrands);
        
        // Filter results by year if provided
        let filteredStrands = allStrands;
        if (year) {
          filteredStrands = allStrands.filter((s: Strand) => s.year === year);
          console.log('Filtered strands by year:', filteredStrands);
        }
        
        setStrands(filteredStrands);
      } catch (error) {
        console.error('Error searching strands:', error);
        setStrandError('Failed to load strands');
        setStrands([]);
      } finally {
        setLoadingStrands(false);
      }
    }, 300),
    []
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" color="primary" gutterBottom>
          Internal Use
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Autocomplete
          fullWidth
          value={selectedStrand}
          onChange={async (_, newValue) => {
            console.log('Selected strand with full details:', newValue);
            
            if (newValue && newValue._id) {
              // If the selected strand has no code, fetch the full details
              if (!newValue.code) {
                console.log('Selected strand has no code, fetching full details');
                const fullStrand = await fetchStrandById(newValue._id);
                if (fullStrand) {
                  console.log('Got full strand with code:', fullStrand.code);
                  setSelectedStrand(fullStrand);
                  onActivityChange(fullStrand.name, fullStrand.code || '');
                  return;
                }
              }
              
              // Otherwise use what we have
              setSelectedStrand(newValue);
              onActivityChange(newValue.name || '', newValue.code || '');
            } else {
              // No strand selected
              setSelectedStrand(null);
              onActivityChange('', '');
            }
          }}
          onInputChange={(_, value) => searchStrands(value)}
          options={strands}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return `${option.name} (${option.year})` || '';
          }}
          loading={loadingStrands}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Activity"
              error={!!strandError}
              helperText={strandError}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingStrands && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <div>
                <Typography variant="body1">{option.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.code && (
                    <Typography variant="caption" sx={{ 
                      bgcolor: 'primary.light', 
                      color: 'primary.contrastText',
                      px: 0.8,
                      py: 0.2,
                      borderRadius: 1,
                      fontWeight: 'medium'
                    }}>
                      {option.code}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {option.year} • {option.type} • {option.eventType}
                  </Typography>
                </Box>
              </div>
            </li>
          )}
        />
        
        {selectedStrand?.code && (
          <Box sx={{ mt: 1, ml: 1 }}>
            <Typography variant="caption" color="primary">
              Strand Code: <strong>{selectedStrand.code}</strong>
            </Typography>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <Autocomplete
          fullWidth
          value={selectedIMCCode}
          onChange={(_, newValue) => {
            setSelectedIMCCode(newValue);
            onIMCCodeChange(newValue?.label || '');
          }}
          options={imcOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          renderInput={(params) => (
            <TextField
              {...params}
              label="IMC Code"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Tooltip title="Internal accounting code">
                    <Info size={20} className="text-gray-400" />
                  </Tooltip>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <div>
                <Typography variant="body1">{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.code}
                  {option.description && ` • ${option.description}`}
                </Typography>
              </div>
            </li>
          )}
        />
      </Grid>
    </Grid>
  );
};

export default InternalUseSection;